import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Brush
} from 'recharts';
import {
    Archive, Download, Calendar, Clock, Loader2, Inbox,
    AlertTriangle, LineChart as LineChartIcon, TrendingUp,
    Thermometer, CircleGauge, Droplets, Waves, Flame,
    Filter, Layers, Info, ChevronRight, Eye, EyeOff,
    FileSpreadsheet, CheckCircle2, XCircle, Scale, Hash
} from 'lucide-react';

// ============================================
// ✅ Константы
// ============================================
const NS_VALUE = -1; // Для Карат-307 НС = -1

const PARAM_CATEGORIES = {
    temperatura: { name: 'Температура', icon: Thermometer, unit: '°C', color: '#ef4444' },
    davlenie: { name: 'Давление', icon: CircleGauge, unit: 'МПа', color: '#06b6d4' },
    energi: { name: 'Энергия', icon: Flame, unit: 'Гкал', color: '#f97316' },
    massa: { name: 'Масса', icon: Scale, unit: 'т', color: '#6366f1' },
    time: { name: 'Время', icon: Clock, unit: 'час', color: '#3b82f6' },
};

const CHART_COLORS = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4',
    '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b',
    '#84cc16', '#0ea5e9', '#d946ef', '#64748b', '#dc2626'
];

// ============================================
// ✅ Динамическое построение колонок (ИСПРАВЛЕНО)
// ============================================
const buildKaratColumns = (sampleRecord) => {
    if (!sampleRecord) return [];

    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });
    columns.push({ key: 'ns', name: 'НС', type: 'ns' });

    const values = sampleRecord.parameters?.values || {};

    const tempParams = [
        { key: 'T1', name: 'T1' },
        { key: 'T2', name: 'T2' },
        { key: 'Td1', name: 'Td1' },
    ];
    tempParams.forEach((p) => {
        if (values[p.key] !== undefined && values[p.key] !== null) {
            columns.push({ key: p.key, name: p.name, unit: '°C', type: 'value', category: 'temperatura' });
        }
    });

    const pressureParams = [
        { key: 'P1', name: 'P1' },
        { key: 'P2', name: 'P2' },
        { key: 'P3', name: 'P3' },
        { key: 'P4', name: 'P4' },
    ];
    pressureParams.forEach((p) => {
        if (values[p.key] !== undefined && values[p.key] !== null) {
            columns.push({ key: p.key, name: p.name, unit: 'МПа', type: 'value', category: 'davlenie' });
        }
    });

    const energyParams = [
        { key: 'Q1', name: 'Q1' },
        { key: 'Q1min', name: 'Q1min' },
        { key: 'Q1max', name: 'Q1max' },
        { key: 'Q2', name: 'Q2' },
        { key: 'Q2min', name: 'Q2min' },
        { key: 'Q2max', name: 'Q2max' },
        { key: 'Q3', name: 'Q3' },
    ];
    energyParams.forEach((p) => {
        if (values[p.key] !== undefined && values[p.key] !== null) {
            columns.push({ key: p.key, name: p.name, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    const massParams = [
        { key: 'G1', name: 'G1' },
        { key: 'G2', name: 'G2' },
        { key: 'Gd1', name: 'Gd1' },
    ];
    massParams.forEach((p) => {
        if (values[p.key] !== undefined && values[p.key] !== null) {
            columns.push({ key: p.key, name: p.name, unit: 'т', type: 'value', category: 'massa' });
        }
    });

    const timeParams = [
        { key: 'Tmin', name: 'Tmin' },
        { key: 'Tmax', name: 'Tmax' },
        { key: 'Tdt', name: 'Tdt' },
        { key: 'Tf', name: 'Tf' },
        { key: 'Tep', name: 'Tep' },
        { key: 'Narabotka', name: 'Наработка' },
    ];
    timeParams.forEach((p) => {
        if (values[p.key] !== undefined && values[p.key] !== null) {
            columns.push({ key: p.key, name: p.name, unit: 'час', type: 'value', category: 'time' });
        }
    });

    return columns;
};


// ============================================
// ✅ Утилиты форматирования
// ============================================
const formatValue = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE) return 'НС';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

const formatWithComma = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE) return 'НС';
    return num.toFixed(decimals).replace('.', ',');
};

const formatDateTime = (record) => {
    if (!record) return '—';
    const date = record.date || '';
    const time = record.time || '';
    return `${date} ${time}`.trim();
};

// ============================================
// ✅ Компонент графика с фильтрами
// ============================================
const ArchiveChart = ({ records, columns }) => {
    const [selectedCategory, setSelectedCategory] = useState('temperatura');
    const [selectedChannels, setSelectedChannels] = useState([]);

    const allChannels = useMemo(() => {
        return columns.filter(ch => ch.category);
    }, [columns]);

    const categoryChannels = useMemo(() => {
        return allChannels.filter(ch => ch.category === selectedCategory);
    }, [allChannels, selectedCategory]);

    const availableCategories = useMemo(() => {
        const cats = new Set(allChannels.map(ch => ch.category));
        return Object.keys(PARAM_CATEGORIES).filter(cat => cats.has(cat));
    }, [allChannels]);

    useEffect(() => {
        if (categoryChannels.length > 0) {
            setSelectedChannels([categoryChannels[0].key]);
        } else {
            setSelectedChannels([]);
        }
    }, [selectedCategory, categoryChannels]);

    useEffect(() => {
        if (availableCategories.length > 0 && !availableCategories.includes(selectedCategory)) {
            setSelectedCategory(availableCategories[0]);
        }
    }, [availableCategories]);

    const chartData = useMemo(() => {
        if (!records || records.length === 0) return [];

        // ✅ Массив records уже отсортирован по возрастанию дат (старые → новые).
        // Не разворачиваем: слева старые даты, справа новые (старшие по времени).
        return records.map(record => {
            const datetime = formatDateTime(record);
            const point = { datetime, datetimeFull: datetime };

            const values = record.parameters?.values || {};

            selectedChannels.forEach(chKey => {
                const val = parseFloat(values[chKey]);
                point[chKey] = (isNaN(val) || val === NS_VALUE) ? null : val;
            });

            return point;
        });
    }, [records, selectedChannels]);

    const toggleChannel = (chKey) => {
        setSelectedChannels(prev =>
            prev.includes(chKey) ? prev.filter(k => k !== chKey) : [...prev, chKey]
        );
    };

    const selectAllChannels = () => setSelectedChannels(categoryChannels.map(ch => ch.key));
    const clearChannels = () => setSelectedChannels([]);

    if (allChannels.length === 0) {
        return (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 text-center">
                <LineChartIcon className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <p className="text-slate-400">Нет параметров для отображения на графике</p>
            </div>
        );
    }

    const categoryInfo = PARAM_CATEGORIES[selectedCategory];

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-4 py-3 bg-slate-700/50 border-b border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="text-white font-semibold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    График: {categoryInfo?.name || selectedCategory}
                    {categoryInfo?.unit && (
                        <span className="text-xs text-slate-400 font-normal">({categoryInfo.unit})</span>
                    )}
                </h3>
            </div>

            <div className="p-4 border-b border-slate-700 space-y-3">
                <div>
                    <label className="block text-slate-400 text-xs mb-2 uppercase tracking-wide flex items-center gap-1">
                        <Filter className="w-3 h-3" />
                        Категория параметра
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {availableCategories.map(catKey => {
                            const cat = PARAM_CATEGORIES[catKey];
                            const CatIcon = cat.icon;
                            const count = allChannels.filter(ch => ch.category === catKey).length;
                            return (
                                <button
                                    key={catKey}
                                    onClick={() => setSelectedCategory(catKey)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-2 ${selectedCategory === catKey
                                        ? 'bg-green-600 text-white'
                                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                        }`}
                                >
                                    <CatIcon className="w-4 h-4" />
                                    <span>{cat.name}</span>
                                    <span className="text-xs opacity-70">({count})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-slate-400 text-xs uppercase tracking-wide flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            Каналы ({selectedChannels.length} из {categoryChannels.length})
                        </label>
                        <div className="flex gap-2">
                            <button onClick={selectAllChannels} className="text-xs text-green-400 hover:text-green-300 transition">Выбрать все</button>
                            <button onClick={clearChannels} className="text-xs text-slate-400 hover:text-slate-300 transition">Сбросить</button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categoryChannels.map((ch, idx) => {
                            const isSelected = selectedChannels.includes(ch.key);
                            const color = CHART_COLORS[idx % CHART_COLORS.length];
                            return (
                                <button
                                    key={ch.key}
                                    onClick={() => toggleChannel(ch.key)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-2 border ${isSelected
                                        ? 'border-transparent text-white'
                                        : 'border-slate-600 bg-slate-900 text-slate-400 hover:border-slate-500'
                                        }`}
                                    style={isSelected ? { backgroundColor: color + '33', borderColor: color } : {}}
                                >
                                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: isSelected ? color : '#475569' }}></span>
                                    <span>{ch.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="p-4">
                {selectedChannels.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <TrendingUp className="w-12 h-12 mb-3" />
                        <p className="text-sm">Выберите хотя бы один канал для отображения графика</p>
                    </div>
                ) : chartData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <Inbox className="w-12 h-12 mb-3" />
                        <p className="text-sm">Нет данных для отображения</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="datetime" stroke="#94a3b8" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                            <YAxis
                                stroke="#94a3b8"
                                tick={{ fontSize: 11 }}
                                tickFormatter={(val) => typeof val === 'number' ? formatWithComma(val, 2) : val}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1e293b',
                                    border: '1px solid #475569',
                                    borderRadius: '8px',
                                    color: '#f1f5f9',
                                    fontSize: '12px'
                                }}
                                labelStyle={{ color: '#94a3b8', fontWeight: 'bold' }}
                                formatter={(value, name) => {
                                    const ch = allChannels.find(c => c.key === name);
                                    const displayName = ch ? ch.name : name;
                                    const unit = ch ? ch.unit : '';
                                    return [value !== null ? formatWithComma(value, 3) : 'НС', `${displayName}, ${unit}`];
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                            <Brush dataKey="datetime" height={25} stroke="#22c55e" fill="#1e293b" travellerWidth={8} />
                            {selectedChannels.map((chKey, idx) => {
                                const colorIdx = categoryChannels.findIndex(c => c.key === chKey);
                                const color = CHART_COLORS[(colorIdx >= 0 ? colorIdx : idx) % CHART_COLORS.length];
                                return (
                                    <Line
                                        key={chKey}
                                        type="monotone"
                                        dataKey={chKey}
                                        name={chKey}
                                        stroke={color}
                                        strokeWidth={2}
                                        dot={chartData.length <= 50}
                                        activeDot={{ r: 5, strokeWidth: 2 }}
                                        connectNulls={false}
                                        isAnimationActive={false}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

// ============================================
// ✅ Компонент раскрытого списка НС
// ============================================
const NSListExpanded = ({ messages, onClose }) => {
    if (!messages || messages.length === 0) return null;
    const errors = messages.filter(m => m.type === 'error');
    const warnings = messages.filter(m => m.type === 'warning');

    return (
        <tr>
            <td colSpan={100} className="p-0">
                <div className="bg-slate-950 border-y border-yellow-500/30">
                    <div className="flex items-center justify-between px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                            <span className="text-yellow-200 font-semibold text-sm">
                                Нештатные ситуации ({messages.length})
                            </span>
                            {errors.length > 0 && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    Аварий: {errors.length}
                                </span>
                            )}
                            {warnings.length > 0 && (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded text-xs flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Предупреждений: {warnings.length}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white transition p-1 rounded hover:bg-slate-700"
                            title="Закрыть"
                        >
                            <XCircle className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto custom-scrollbar">
                        {errors.length > 0 && (
                            <div className="px-4 py-2">
                                <div className="text-red-400 text-xs font-semibold mb-2 uppercase tracking-wide flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    Аварии ({errors.length})
                                </div>
                                <div className="space-y-1">
                                    {errors.map((msg, idx) => (
                                        <div key={`err-${idx}`} className="flex items-start gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-sm">
                                            <span className="text-red-400 flex-shrink-0">🔴</span>
                                            <span className="text-red-200">{msg.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {warnings.length > 0 && (
                            <div className="px-4 py-2">
                                <div className="text-yellow-400 text-xs font-semibold mb-2 uppercase tracking-wide flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Предупреждения ({warnings.length})
                                </div>
                                <div className="space-y-1">
                                    {warnings.map((msg, idx) => (
                                        <div key={`warn-${idx}`} className="flex items-start gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-sm">
                                            <span className="text-yellow-400 flex-shrink-0">🟡</span>
                                            <span className="text-yellow-200">{msg.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
};

// ============================================
// ✅ Генерация списка НС
// ============================================
const buildNSMessagesForRecord = (record) => {
    if (!record) return [];

    const errorsArray = record.parameters?.errors || [];

    return errorsArray.map(text => ({
        type: text.includes('ОШИБКА') ? 'error' : 'warning',
        text
    }));
};

// ============================================
// ✅ Экспорт в Excel
// ============================================
const exportToExcel = (records, columns, archiveType, dateFrom, dateTo, deviceName, serialNumber, address) => {
    if (!records || records.length === 0) {
        alert('Нет данных для экспорта');
        return;
    }

    const headers = columns.map(col => {
        if (col.type === 'datetime') return 'Дата/Время';
        if (col.type === 'ns') return 'НС';
        return `${col.name}, ${col.unit}`;
    });

    // ✅ Экспортируем в том же порядке, что и в таблице (по убыванию дат — свежие сверху)
    const sortedRecords = [...records].reverse();

    const rows = sortedRecords.map(record => {
        const values = record.parameters?.values || {};

        const rowValues = columns.map(col => {
            if (col.type === 'datetime') {
                return formatDateTime(record);
            }
            if (col.type === 'ns') {
                const nsMessages = buildNSMessagesForRecord(record);
                return nsMessages.length;
            }
            const val = parseFloat(values[col.key]);
            if (isNaN(val)) return '';
            if (val === NS_VALUE) return 'НС';
            return formatWithComma(val, 3);
        });

        return rowValues.map(v => `"${v}"`).join(';');
    });

    const archiveTypeNames = {
        'hour': 'Часовой',
        'day': 'Суточный',
        'month': 'Месячный',
        'integral': 'Интегральный'
    };

    const metaInfo = [
        `Карат-307`,
        `Серийный номер: ${serialNumber || '—'}`,
        `Адрес: ${address || '—'}`,
        `Тип архива: ${archiveTypeNames[archiveType] || archiveType}`,
        `Период: ${new Date(dateFrom).toLocaleDateString('ru-RU')} - ${new Date(dateTo).toLocaleDateString('ru-RU')}`,
        `Количество записей: ${records.length}`,
        '',
        headers.map(h => `"${h}"`).join(';')
    ];

    const csvContent = [...metaInfo, ...rows].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.setAttribute('href', url);
    link.setAttribute('download', `karat307_${deviceName}_${archiveType}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// ============================================
// ✅ Главный компонент вкладки "Архив"
// ============================================
const ArchiveTab = ({ device, deviceName }) => {
    const [archiveType, setArchiveType] = useState('day');
    const [loadedArchiveType, setLoadedArchiveType] = useState('day');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [archiveData, setArchiveData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedNSRecord, setExpandedNSRecord] = useState(null);
    const [showChart, setShowChart] = useState(true);

    const [deviceInfo, setDeviceInfo] = useState(null);

    const PAGE_SIZE = 50;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const tableContainerRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL;

    const archiveTypes = [
        { id: 'hour', name: 'Часовой', icon: Clock },
        { id: 'day', name: 'Суточный', icon: Calendar },
        { id: 'month', name: 'Месячный', icon: Calendar },
        { id: 'integral', name: 'Интегральный', icon: LineChartIcon },
    ];

    const archiveTypeNames = {
        'hour': 'Часовой',
        'day': 'Суточный',
        'month': 'Месячный',
        'integral': 'Интегральный'
    };

    useEffect(() => {
        loadDeviceInfo();
    }, [deviceName]);

    const loadDeviceInfo = async () => {
        if (!deviceName) return;

        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/devices/karat307/${deviceName}/info`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            console.log('Karat-307 DeviceInfo:', response.data);
            setDeviceInfo(response.data);
        } catch (err) {
            console.error('Ошибка загрузки информации об устройстве:', err);
        }
    };

    const loadArchive = async () => {
        if (!deviceName) return;

        setIsLoading(true);
        setError(null);
        setExpandedNSRecord(null);
        setVisibleCount(PAGE_SIZE);
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/devices/karat307/${deviceName}/archive`,
                {
                    params: { type: archiveType, from: dateFrom, to: dateTo },
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            console.log('📊 Karat-307 Archive:', response.data);
            setArchiveData(response.data);
            setLoadedArchiveType(archiveType);
        } catch (err) {
            console.error('Ошибка загрузки архива:', err);
            setError(err.response?.data?.message || 'Не удалось загрузить архивные данные');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadArchive();
    }, [deviceName]);

    useEffect(() => {
        if (!sentinelRef.current || !archiveData) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && archiveData) {
                    const totalRecs = archiveData.records?.length || 0;
                    if (visibleCount < totalRecs && !isLoadingMore) {
                        setIsLoadingMore(true);
                        setTimeout(() => {
                            setVisibleCount(prev => Math.min(prev + PAGE_SIZE, totalRecs));
                            setIsLoadingMore(false);
                        }, 150);
                    }
                }
            },
            {
                root: tableContainerRef.current,
                rootMargin: '200px',
                threshold: 0.1
            }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [archiveData, visibleCount, isLoadingMore]);

    const records = archiveData?.records || [];
    const totalRecords = records.length;

    const columns = useMemo(() => {
        if (!records || records.length === 0) return [];
        const sampleRecord = records[0];
        return buildKaratColumns(sampleRecord);
    }, [records]);

    // ✅ Таблица: разворачиваем массив, чтобы сверху были самые свежие (старшие по времени) даты.
    // Таким образом порядок строк идёт по убыванию даты.
    const visibleRecords = useMemo(() => {
        return [...records].reverse().slice(0, visibleCount);
    }, [records, visibleCount]);

    const hasMore = visibleCount < totalRecords;
    const isChartAvailable = loadedArchiveType !== 'integral';
    const isArchiveTypeChanged = archiveType !== loadedArchiveType;

    const handleNSCellClick = (recordIdx) => {
        if (expandedNSRecord === recordIdx) {
            setExpandedNSRecord(null);
        } else {
            setExpandedNSRecord(recordIdx);
        }
    };

    const handleShowAll = () => setVisibleCount(totalRecords);

    const handleShowLess = () => {
        setVisibleCount(PAGE_SIZE);
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    };

    const getAddress = () => {
        if (!device) return '—';
        const parts = [];
        if (device.city) parts.push(device.city);
        if (device.street) parts.push(device.street);
        if (device.houseNumber) parts.push(`дом ${device.houseNumber}`);
        return parts.length > 0 ? parts.join(', ') : '—';
    };

    const getSerialNumber = () => {
        return deviceInfo?.serialNumber || '—';
    };

    const getCellValue = (record, col) => {
        if (col.type === 'datetime') {
            return formatDateTime(record);
        }
        if (col.type === 'ns') {
            const nsMessages = buildNSMessagesForRecord(record);
            return nsMessages.length;
        }

        const values = record.parameters?.values || {};
        const val = parseFloat(values[col.key]);

        if (isNaN(val)) return '—';
        if (val === NS_VALUE) return 'НС';
        return formatWithComma(val, 3);
    };

    return (
        <div className="space-y-4">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Archive className="w-5 h-5 text-green-400" />
                    Архивные данные
                </h3>

                <div className="mb-4">
                    <label className="block text-slate-400 text-xs mb-2">Тип архива</label>
                    <div className="flex flex-wrap gap-2">
                        {archiveTypes.map(p => {
                            const Icon = p.icon;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setArchiveType(p.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${archiveType === p.id
                                        ? 'bg-green-600 text-white'
                                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{p.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">С даты</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">По дату</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={loadArchive}
                            disabled={isLoading}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Загрузка...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Загрузить
                                </>
                            )}
                        </button>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => exportToExcel(
                                records,
                                columns,
                                loadedArchiveType,
                                dateFrom,
                                dateTo,
                                deviceName?.name || '—',
                                getSerialNumber(),
                                getAddress()
                            )}
                            disabled={!archiveData || records.length === 0}
                            className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Выгрузить архив в Excel (CSV)"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                            Экспорт в Excel
                        </button>
                    </div>
                </div>

                {isArchiveTypeChanged && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-yellow-200 text-sm font-medium">
                                Выбран тип архива: <strong>{archiveTypeNames[archiveType]}</strong>
                            </p>
                            <p className="text-yellow-300/80 text-xs mt-1">
                                Отображаются данные за загруженный тип: <strong>{archiveTypeNames[loadedArchiveType]}</strong>.
                                Нажмите "Загрузить" для обновления.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {!archiveData || totalRecords === 0 ? (
                <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                    <Inbox className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400">
                        {isLoading ? 'Загрузка данных...' : 'Нет данных за выбранный период'}
                    </p>
                </div>
            ) : (
                <>
                    {isChartAvailable && showChart && (
                        <div className="relative">
                            <button
                                onClick={() => setShowChart(false)}
                                className="absolute top-2 right-2 z-10 p-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition flex items-center gap-1"
                                title="Скрыть график"
                            >
                                <EyeOff className="w-4 h-4" />
                                <span className="text-xs">Скрыть</span>
                            </button>
                            <ArchiveChart records={records} columns={columns} />
                        </div>
                    )}

                    {isChartAvailable && !showChart && (
                        <button
                            onClick={() => setShowChart(true)}
                            className="w-full px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2"
                        >
                            <Eye className="w-4 h-4" />
                            Показать график
                        </button>
                    )}

                    {!isChartAvailable && (
                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                            <Info className="w-4 h-4" />
                            График недоступен для интегрального архива. Используйте таблицу ниже.
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-2">
                        <div className="text-slate-400 text-sm flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            Показано <span className="text-white font-semibold">{Math.min(visibleCount, totalRecords)}</span> из{' '}
                            <span className="text-white font-semibold">{totalRecords}</span> записей
                        </div>
                        <div className="flex gap-2">
                            {visibleCount > PAGE_SIZE && (
                                <button
                                    onClick={handleShowLess}
                                    className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs font-medium transition flex items-center gap-1"
                                >
                                    <ChevronRight className="w-3 h-3 rotate-[-90deg]" />
                                    Свернуть
                                </button>
                            )}
                            {hasMore && (
                                <button
                                    onClick={handleShowAll}
                                    className="px-3 py-1 bg-green-600/30 hover:bg-green-600/50 text-green-300 rounded text-xs font-medium transition"
                                >
                                    Показать все ({totalRecords})
                                </button>
                            )}
                        </div>
                    </div>

                    <div
                        ref={tableContainerRef}
                        className="bg-slate-800 rounded-xl border border-slate-700 overflow-auto custom-scrollbar"
                        style={{ maxHeight: 'calc(100vh - 380px)', minHeight: '400px' }}
                    >
                        <table className="w-full text-sm">
                            <thead className="bg-slate-700/80 backdrop-blur-sm sticky top-0 z-20 shadow-md">
                                <tr>
                                    {columns.map((col, idx) => (
                                        <th
                                            key={idx}
                                            className={`px-3 py-3 font-semibold whitespace-nowrap border-r border-slate-600 last:border-r-0 bg-slate-800 text-slate-200 ${col.type === 'datetime' || col.type === 'ns' ? 'text-left' : 'text-right'
                                                }`}
                                        >
                                            <div className="flex flex-col">
                                                <span>{col.type === 'datetime' ? 'Дата/Время' : col.type === 'ns' ? 'НС' : col.name}</span>
                                                {col.unit && col.type !== 'datetime' && col.type !== 'ns' && (
                                                    <span className="text-xs text-slate-400 font-normal">{col.unit}</span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRecords.map((record, recordIdx) => {
                                    const isExpanded = expandedNSRecord === recordIdx;
                                    const nsMessages = buildNSMessagesForRecord(record);
                                    const nsCount = nsMessages.length;

                                    return (
                                        <React.Fragment key={recordIdx}>
                                            <tr className={`border-t border-slate-700 hover:bg-slate-700/30 transition ${isExpanded ? 'bg-yellow-500/5' : ''}`}>
                                                {columns.map((col, colIdx) => {
                                                    const value = getCellValue(record, col);
                                                    const isNS = value === 'НС';
                                                    const isNSCount = col.type === 'ns';

                                                    if (isNSCount) {
                                                        return (
                                                            <td
                                                                key={colIdx}
                                                                className={`px-3 py-2 whitespace-nowrap border-r border-slate-700 text-center ${nsCount > 0 ? 'cursor-pointer hover:bg-yellow-500/20 transition' : ''}`}
                                                                onClick={() => nsCount > 0 && handleNSCellClick(recordIdx)}
                                                                title={nsCount > 0 ? 'Нажмите для просмотра списка НС' : ''}
                                                            >
                                                                {nsCount > 0 ? (
                                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded font-semibold transition ${isExpanded
                                                                        ? 'bg-yellow-500/30 text-yellow-200 ring-2 ring-yellow-500/50'
                                                                        : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                                                                        }`}>
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                        <span>({nsCount})</span>
                                                                        <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                                    </span>
                                                                ) : (
                                                                    <CheckCircle2 className="w-4 h-4 text-green-400 inline-block" />
                                                                )}
                                                            </td>
                                                        );
                                                    }

                                                    return (
                                                        <td
                                                            key={colIdx}
                                                            className={`px-3 py-2 whitespace-nowrap border-r border-slate-700 last:border-r-0 ${col.type === 'datetime' || col.type === 'ns' ? 'text-left text-white' : 'text-right font-mono'
                                                                } ${isNS ? 'text-red-400 font-bold bg-red-500/10' : 'text-white'}`}
                                                        >
                                                            {value}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {isExpanded && (
                                                <NSListExpanded
                                                    messages={nsMessages}
                                                    onClose={() => setExpandedNSRecord(null)}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                                {hasMore && (
                                    <tr ref={sentinelRef}>
                                        <td colSpan={columns.length} className="p-4 text-center">
                                            {isLoadingMore ? (
                                                <div className="flex items-center justify-center gap-2 text-slate-400">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span className="text-sm">Загрузка ещё {Math.min(PAGE_SIZE, totalRecords - visibleCount)} записей...</span>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setVisibleCount(prev => Math.min(prev + PAGE_SIZE, totalRecords))}
                                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition"
                                                >
                                                    Показать ещё ({Math.min(PAGE_SIZE, totalRecords - visibleCount)} из {totalRecords - visibleCount})
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )}

                                {!hasMore && totalRecords > PAGE_SIZE && (
                                    <tr>
                                        <td colSpan={columns.length} className="p-3 text-center text-slate-500 text-xs">
                                            — Конец списка • Показано все {totalRecords} записей —
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ArchiveTab;